// components/FilterChips/FilterChips.tsx — the shared "chip row that genuinely
// filters" control. Introduced in slice-4-xl's Live fix round (S4): Videos'
// VideoChips and Live's LiveChips were identical apart from their generic type
// and aria-label — this is the one implementation, generic over the chip id
// type. Both feature chip rows are now thin wrappers (see
// src/features/videos/VideoChips.tsx and src/features/live/LiveChips.tsx) so
// import sites don't need to change.
import styled from 'styled-components';
import { media } from '@/theme/media';
import { Icon, type IconName } from '@/icons';

const LiveDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ theme }) => theme.color.danger};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;

  ${media.mobile`
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
    padding-bottom: 2px;
  `}
`;

const Chip = styled.button<{ $active: boolean }>`
  cursor: pointer;
  padding: 6px 14px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  border: 1px solid ${({ theme, $active }) => ($active ? 'transparent' : theme.color.glassBorder)};
  color: ${({ theme, $active }) => ($active ? theme.color.onAccent : theme.color.textPrimary)};
  background: ${({ theme, $active }) => ($active ? theme.color.accentCyan : theme.color.glassBg)};
  transition:
    filter 120ms ease,
    background 120ms ease;
  /* Hover is a slight brightening, never a stroke change (owner rule). */
  &:hover {
    filter: brightness(1.12);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: 2px;
  }
`;

export interface FilterChipDef<T extends string> {
  id: T;
  label: string;
  /** Optional leading icon (Videos chips, 2026-09-26). */
  icon?: IconName;
  /** A small red dot before the label — the Live chip. */
  dot?: boolean;
}

export interface FilterChipsProps<T extends string> {
  chips: FilterChipDef<T>[];
  active: T;
  onChange: (id: T) => void;
  /** e.g. "Video filters" or "Live filters" — read by screen readers on the
   *  chip row's `role="group"` container. */
  ariaLabel: string;
}

export function FilterChips<T extends string>({
  chips,
  active,
  onChange,
  ariaLabel,
}: FilterChipsProps<T>) {
  return (
    <Row role="group" aria-label={ariaLabel}>
      {chips.map((c) => (
        <Chip
          key={c.id}
          type="button"
          $active={active === c.id}
          aria-pressed={active === c.id}
          onClick={() => onChange(c.id)}
        >
          {c.dot && <LiveDot aria-hidden="true" />}
          {c.icon && <Icon name={c.icon} size={14} />}
          {c.label}
        </Chip>
      ))}
    </Row>
  );
}
